// src/utils/images.js - FIXED: WebP + Responsive Images + Performance

/**
 * PERFORMANCE OPTIMIZED Image Utility System for Connect & Co
 * 
 * FIXES APPLIED:
 * 1. Fixed WebP detection to always prefer WebP in modern browsers
 * 2. Added automatic responsive image sizing
 * 3. Implemented smart image optimization based on display context
 * 4. Added Google Cloud Storage image transformation parameters
 */

// Environment Detection
const isDevelopment = import.meta.env.MODE === 'development';
const isProduction = import.meta.env.MODE === 'production';

// Configuration
const IMAGE_CONFIG = {
  // Google Cloud Storage Configuration
  GCS_BUCKET: 'connectco-storage',
  GCS_BASE_URL: 'https://storage.googleapis.com/connectco-storage',
  
  // Digital Ocean Configuration
  DO_BASE_URL: '',
  DO_PRODUCTION_URL: 'https://connectco.app',
  
  // Migration Control
  USE_GOOGLE_CLOUD_STORAGE: true,
  
  // Debug and Safety Features
  DEBUG_MODE: false,
  SHOW_FIRST_TIME_URLS: true,
  SHOW_ERRORS_ONLY: true,
  FALLBACK_TO_LOCAL: true,
  
  // Performance Settings - ENHANCED
  ENABLE_WEBP: true,
  FORCE_WEBP_IN_PRODUCTION: true, // 🔥 NEW: Force WebP in production
  DEFAULT_QUALITY: 85, // 🔥 IMPROVED: Higher quality for better compression
  
  // 🔥 NEW: Responsive image breakpoints
  ORIG_RESPONSIVE_BREAKPOINTS: {
    thumbnail: { width: 150, height: 150, quality: 75 },
    small: { width: 300, height: 200, quality: 80 },
    medium: { width: 600, height: 400, quality: 85 },
    large: { width: 1200, height: 800, quality: 90 },
    hero: { width: 1920, height: 1080, quality: 85 }
  },

  RESPONSIVE_BREAKPOINTS: {
    thumbnail: { width: 150, height: 150, quality: 75 },
    small: { width: 364, height: 243, quality: 80 },     // 🔥 EXACT PageSpeed dimensions
    medium: { width: 600, height: 400, quality: 85 },
    large: { width: 1200, height: 800, quality: 90 },
    hero: { width: 1920, height: 1080, quality: 85 }
  },
  
  // 🔥 NEW: Context-specific image sizes
  ORIG_CONTEXT_SIZES: {
    'blog-thumbnail': 'small',
    'blog-hero': 'large',
    'author-avatar': 'thumbnail',
    'author-avatar-large': 'small',
    'event-card': 'medium',
    'event-hero': 'hero',
    'venue-card': 'medium'
  },

  CONTEXT_SIZES: {
    'blog-thumbnail': 'small',    // ✅ Good
    'blog-hero': 'medium',        // 🔥 CHANGE: from 'large' to 'medium'
    'author-avatar': 'thumbnail', // ✅ Good
    'author-avatar-large': 'small', // ✅ Good
    'event-card': 'small',        // 🔥 CHANGE: from 'medium' to 'small'
    'event-hero': 'large',        // ✅ Good for hero images
    'venue-card': 'small'         // 🔥 CHANGE: from 'medium' to 'small'
  },
  
  // Fallback Images
  FALLBACK_IMAGES: {
    blog: '/images/placeholders/blog-placeholder.jpg',
    author: '/images/placeholders/author-placeholder.jpg', 
    event: '/images/placeholders/event-placeholder.jpg',
    venue: '/images/placeholders/venue-placeholder.jpg'
  }
};

// URL Cache and tracking
const URL_CACHE = new Map();
const LOGGED_URLS = new Set();
const FAILED_URLS = new Set();

/**
 * Smart logger
 */
function logImageDebug(level, message, ...args) {
  if (level === 'error') {
    console.error(`[ImageUtils-ERROR] ${message}`, ...args);
    return;
  }
  
  if (level === 'warn' && isDevelopment) {
    console.warn(`[ImageUtils-WARN] ${message}`, ...args);
    return;
  }
  
  if (IMAGE_CONFIG.DEBUG_MODE && isDevelopment) {
    const prefix = `[ImageUtils-${level.toUpperCase()}]`;
    if (level === 'info') {
      console.info(`${prefix} ${message}`, ...args);
    } else {
      console.log(`${prefix} ${message}`, ...args);
    }
  }
}

/**
 * 🔥 FIXED: Enhanced WebP detection - Always use WebP in modern browsers
 */
let webpSupported = null;
function supportsWebP() {
  // 🔥 FIX: During SSR, assume WebP support (modern browsers)
  if (typeof window === 'undefined') {
    return isProduction; // Use WebP in production, skip in development SSR
  }
  
  if (webpSupported !== null) return webpSupported;
  
  // 🔥 ENHANCED: Better WebP detection
  try {
    const canvas = document.createElement('canvas');
    if (canvas.getContext && canvas.getContext('2d')) {
      // Test both lossy and lossless WebP
      const testData = canvas.toDataURL('image/webp', 0.8);
      webpSupported = testData.indexOf('data:image/webp') === 0;
      
      // 🔥 FALLBACK: If canvas test fails, check user agent
      if (!webpSupported) {
        const userAgent = navigator.userAgent;
        webpSupported = /Chrome|Firefox|Edge|Opera|Safari/.test(userAgent) &&
                        !/(Chrome\/[0-8]\.)|(Firefox\/[0-6]\.)/.test(userAgent);
      }
    } else {
      // 🔥 FALLBACK: Modern browser detection
      webpSupported = 'createImageBitmap' in window;
    }
  } catch (error) {
    logImageDebug('warn', 'WebP detection failed, using fallback');
    webpSupported = isProduction; // Default to true in production
  }
  
  if (IMAGE_CONFIG.DEBUG_MODE) {
    logImageDebug('info', `WebP support detected: ${webpSupported}`);
  }
  
  return webpSupported;
}

/**
 * 🔥 NEW: Get optimal image size for context
 */
function getOptimalImageSize(context = 'medium', customSize = null) {
  if (customSize) {
    return customSize;
  }
  
  const sizeKey = IMAGE_CONFIG.CONTEXT_SIZES[context] || context;
  const size = IMAGE_CONFIG.RESPONSIVE_BREAKPOINTS[sizeKey] || IMAGE_CONFIG.RESPONSIVE_BREAKPOINTS.medium;
  
   // 🔍 DEBUG: Add this logging
   console.log('🔍 getOptimalImageSize DEBUG:', {
    inputContext: context,
    mappedSizeKey: sizeKey,
    foundSize: size,
    contextSizesHasKey: IMAGE_CONFIG.CONTEXT_SIZES.hasOwnProperty(context),
    breakpointsHasKey: IMAGE_CONFIG.RESPONSIVE_BREAKPOINTS.hasOwnProperty(sizeKey),
    availableContexts: Object.keys(IMAGE_CONFIG.CONTEXT_SIZES),
    availableBreakpoints: Object.keys(IMAGE_CONFIG.RESPONSIVE_BREAKPOINTS)
  });
  

  return {
    width: size.width,
    height: size.height,
    quality: size.quality
  };
}

/**
 * 🔥 NEW: Build Google Cloud Storage transformation URL
 */
function origbuildGCSTransformationUrl(baseUrl, transformations) {
  if (!IMAGE_CONFIG.USE_GOOGLE_CLOUD_STORAGE) {
    return baseUrl;
  }
  
  const params = new URLSearchParams();
  
  // Add transformation parameters
  if (transformations.width) params.append('w', transformations.width);
  if (transformations.height) params.append('h', transformations.height);
  if (transformations.quality) params.append('q', transformations.quality);
  if (transformations.format) params.append('f', transformations.format);
  
  // Add fit parameter for better resizing
  if (transformations.width && transformations.height) {
    params.append('fit', 'cover'); // Maintain aspect ratio and cover dimensions
  }
  
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

function buildGCSTransformationUrl(baseUrl, transformations) {
  if (!IMAGE_CONFIG.USE_GOOGLE_CLOUD_STORAGE) {
    return baseUrl;
  }


    // 🔍 DEBUG: Log what transformations are being passed
    console.log('🔍 buildGCSTransformationUrl called with:', {
      baseUrl,
      transformations,
      hasWidth: !!transformations.width,
      hasHeight: !!transformations.height
    });
  
  
  // 🔥 FIX: Clean existing parameters to avoid duplication
  const urlParts = baseUrl.split('?');
  const cleanUrl = urlParts[0];
  
  const params = new URLSearchParams();
  
  // Add transformation parameters
  if (transformations.width) params.append('w', transformations.width);
  if (transformations.height) params.append('h', transformations.height);
  if (transformations.quality) params.append('q', transformations.quality);
  if (transformations.format) params.append('f', transformations.format);
  
  // Add fit parameter for better resizing
  if (transformations.width && transformations.height) {
    params.append('fit', 'cover');
  }

  const queryString = params.toString();
  return queryString ? `${cleanUrl}?${queryString}` : cleanUrl;
}



/**
 * Smart URL analysis with caching
 */
function analyzeImageInput(input) {
  if (URL_CACHE.has(input)) {
    return URL_CACHE.get(input);
  }
  
  let result;
  
  if (!input) {
    result = { type: 'empty', originalInput: input, finalPath: null };
  } else if (input.startsWith(IMAGE_CONFIG.GCS_BASE_URL)) {
    result = { type: 'full_gcs_url', originalInput: input, finalPath: input };
  } else if (input.startsWith('http://') || input.startsWith('https://')) {
    result = { type: 'external_url', originalInput: input, finalPath: input };
  } else if (input.startsWith('//')) {
    result = { type: 'protocol_relative', originalInput: input, finalPath: input };
  } else {
    let normalizedPath = input.startsWith('/') ? input.slice(1) : input;
    result = { type: 'relative_path', originalInput: input, finalPath: normalizedPath };
  }
  
  URL_CACHE.set(input, result);
  
  if (IMAGE_CONFIG.SHOW_FIRST_TIME_URLS && !LOGGED_URLS.has(input)) {
    logImageDebug('info', `🆕 New URL processed: "${input}" → ${result.type}`);
    LOGGED_URLS.add(input);
  }
  
  return result;
}

/**
 * Base URL generator (cached)
 */
let cachedBaseUrl = null;
function getImageBaseUrl() {
  if (cachedBaseUrl) return cachedBaseUrl;
  
  try {
    if (IMAGE_CONFIG.USE_GOOGLE_CLOUD_STORAGE) {
      cachedBaseUrl = IMAGE_CONFIG.GCS_BASE_URL;
    } else if (isDevelopment) {
      cachedBaseUrl = IMAGE_CONFIG.DO_BASE_URL;
    } else {
      cachedBaseUrl = IMAGE_CONFIG.DO_PRODUCTION_URL;
    }
  } catch (error) {
    logImageDebug('error', 'Error getting base URL:', error);
    cachedBaseUrl = isDevelopment ? '' : IMAGE_CONFIG.DO_PRODUCTION_URL;
  }
  
  return cachedBaseUrl;
}

/**
 * 🔥 ENHANCED: Main image URL generator with responsive optimization
 */
export function getImageUrl(imagePath, options = {}) {
  const {
    quality = IMAGE_CONFIG.DEFAULT_QUALITY,
    format = 'auto',
    width = null,
    height = null,
    imageType = 'generic',
    context = 'medium', // 🔥 NEW: Context for smart sizing
    responsive = true // 🔥 NEW: Enable responsive sizing
  } = options;
  
  try {
    if (!imagePath) {
      return IMAGE_CONFIG.FALLBACK_IMAGES[imageType] || IMAGE_CONFIG.FALLBACK_IMAGES.blog;
    }

    // Enhanced cache key including context
    const cacheKey = `${imagePath}-${JSON.stringify(options)}`;
    if (URL_CACHE.has(cacheKey)) {
      return URL_CACHE.get(cacheKey);
    }

    const analysis = analyzeImageInput(imagePath);
    
    // If it's already a full URL, return it as-is (unless we want to add transformations)
    if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
      // 🔥 ENHANCED: Even for full URLs, add transformations if it's a GCS URL
      if (analysis.type === 'full_gcs_url' && responsive) {
        const optimalSize = getOptimalImageSize(context, { width, height, quality });
        const transformations = {
          width: optimalSize.width,
          height: optimalSize.height,
          quality: optimalSize.quality
        };
        
        // 🔥 FIX: Add WebP format if supported
        if (format === 'auto' && supportsWebP()) {
          transformations.format = 'webp';
        }
        
        const transformedUrl = buildGCSTransformationUrl(analysis.finalPath, transformations);
        URL_CACHE.set(cacheKey, transformedUrl);
        return transformedUrl;
      }
      
      URL_CACHE.set(cacheKey, analysis.finalPath);
      return analysis.finalPath;
    }

    // If input was empty
    if (analysis.type === 'empty') {
      const fallback = IMAGE_CONFIG.FALLBACK_IMAGES[imageType] || IMAGE_CONFIG.FALLBACK_IMAGES.blog;
      URL_CACHE.set(cacheKey, fallback);
      return fallback;
    }

    // Handle relative path
    let finalPath = analysis.finalPath;
    
    // 🔥 ENHANCED: WebP optimization with better logic
    let useWebP = false;
    if (format === 'auto' && IMAGE_CONFIG.ENABLE_WEBP) {
      useWebP = supportsWebP();
      
      // 🔥 FORCE WebP in production for better performance
      if (IMAGE_CONFIG.FORCE_WEBP_IN_PRODUCTION && isProduction) {
        useWebP = true;
      }
    }
    
    // 🔥 ENHANCED: Smart WebP conversion
    if (useWebP && finalPath.match(/\.(jpg|jpeg|png)$/i)) {
      const pathWithoutExt = finalPath.replace(/\.(jpg|jpeg|png)$/i, '');
      finalPath = `${pathWithoutExt}.webp`;
    }
    
    const baseUrl = getImageBaseUrl();
    let finalUrl;
    
    // URL construction
    if (IMAGE_CONFIG.USE_GOOGLE_CLOUD_STORAGE) {
      finalUrl = `${baseUrl}/${finalPath}`;
      
      // 🔥 NEW: Add responsive transformations for GCS
      if (responsive) {
        const optimalSize = getOptimalImageSize(context, { width, height, quality });
        const transformations = {
          width: optimalSize.width,
          height: optimalSize.height,
          quality: optimalSize.quality
        };
        
        if (useWebP) {
          transformations.format = 'webp';
        }
        
        finalUrl = buildGCSTransformationUrl(finalUrl, transformations);
      }
    } else {
      finalUrl = isDevelopment ? `/${finalPath}` : `${baseUrl}/${finalPath}`;
    }
    
    // Cache the result
    URL_CACHE.set(cacheKey, finalUrl);
    
    return finalUrl;
    
  } catch (error) {
    logImageDebug('error', `Error generating image URL for "${imagePath}":`, error);
    return IMAGE_CONFIG.FALLBACK_IMAGES[imageType] || IMAGE_CONFIG.FALLBACK_IMAGES.blog;
  }
}

/**
 * 🔥 ENHANCED: Blog image URL generator with context-aware sizing
 */
export function getBlogImageUrl(filename, options = {}) {
  if (!filename) {
    return IMAGE_CONFIG.FALLBACK_IMAGES.blog;
  }
  
  const analysis = analyzeImageInput(filename);
  
  // 🔥 DEFAULT: Use medium size for blog images unless specified
  const defaultOptions = {
    imageType: 'blog',
    context: 'blog-thumbnail',
    responsive: true,
    ...options
  };
  
  if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
    return getImageUrl(filename, defaultOptions);
  }
  
  if (filename.includes('images/blog/')) {
    return getImageUrl(filename, defaultOptions);
  }
  
  const imagePath = `images/blog/${filename}`;
  return getImageUrl(imagePath, defaultOptions);
}

/**
 * 🔥 ENHANCED: Author avatar URL generator with smart sizing
 */
export function origgetAuthorImageUrl(filename, options = {}) {
  if (!filename) {
    return IMAGE_CONFIG.FALLBACK_IMAGES.author;
  }
  
  const analysis = analyzeImageInput(filename);
  
  // 🔥 DEFAULT: Use thumbnail size for author avatars
  const defaultOptions = {
    imageType: 'author',
    context: 'author-avatar',
    responsive: true,
    ...options
  };
  
  if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
    return getImageUrl(filename, defaultOptions);
  }
  
  if (filename.includes('images/authors/')) {
    return getImageUrl(filename, defaultOptions);
  }
  
  const imagePath = `images/authors/${filename}`;
  return getImageUrl(imagePath, defaultOptions);
}

export function getAuthorImageUrl(filename, options = {}) {
  if (!filename) {
    return IMAGE_CONFIG.FALLBACK_IMAGES.author;
  }
  
  // 🔥 FIX: Use Google Cloud Storage for consistency
  const analysis = analyzeImageInput(filename);
  
  const defaultOptions = {
    imageType: 'author',
    width: 32,        // Small size for avatars
    height: 32,
    quality: 75,
    responsive: true,
    ...options
  };
  
  if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
    return getImageUrl(filename, defaultOptions);
  }
  
  if (filename.includes('images/authors/')) {
    return getImageUrl(filename, defaultOptions);
  }
  
  // Default: construct GCS path
  const imagePath = `images/authors/${filename}`;
  return getImageUrl(imagePath, defaultOptions);
}


/**
 * 🔥 ENHANCED: Event image URL generator
 */
export function getEventImageUrl(filename, options = {}) {
  if (!filename) {
    return IMAGE_CONFIG.FALLBACK_IMAGES.event;
  }
  
  const analysis = analyzeImageInput(filename);
  
  const defaultOptions = {
    imageType: 'event',
    context: 'event-card',
    responsive: true,
    ...options
  };
  
  if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
    return getImageUrl(filename, defaultOptions);
  }
  
  if (filename.includes('images/events/')) {
    return getImageUrl(filename, defaultOptions);
  }
  
  const imagePath = `images/events/${filename}`;
  return getImageUrl(imagePath, defaultOptions);
}

/**
 * 🔥 ENHANCED: Venue image URL generator
 */
export function getVenueImageUrl(filename, options = {}) {
  if (!filename) {
    return IMAGE_CONFIG.FALLBACK_IMAGES.venue;
  }
  
  const analysis = analyzeImageInput(filename);
  
  const defaultOptions = {
    imageType: 'venue',
    context: 'venue-card',
    responsive: true,
    ...options
  };
  
  if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
    return getImageUrl(filename, defaultOptions);
  }
  
  if (filename.includes('images/venues/')) {
    return getImageUrl(filename, defaultOptions);
  }
  
  const imagePath = `images/venues/${filename}`;
  return getImageUrl(imagePath, defaultOptions);
}

/**
 * 🔥 ENHANCED: Branding image URL generator
 */
export function getBrandingImageUrl(filename, options = {}) {
  if (!filename) {
    return '/images/branding/ampersand_logo.png';
  }
  
  const analysis = analyzeImageInput(filename);
  
  const defaultOptions = {
    imageType: 'branding',
    context: 'small',
    responsive: true,
    ...options
  };
  
  if (analysis.type === 'full_gcs_url' || analysis.type === 'external_url' || analysis.type === 'protocol_relative') {
    return getImageUrl(filename, defaultOptions);
  }
  
  if (filename.includes('images/branding/')) {
    return getImageUrl(filename, defaultOptions);
  }
  
  const imagePath = `images/branding/${filename}`;
  return getImageUrl(imagePath, defaultOptions);
}

/**
 * 🔥 NEW: Get responsive image with multiple sizes
 */
export function getResponsiveImageSet(imagePath, contexts = ['thumbnail', 'small', 'medium'], options = {}) {
  const imageSet = {};
  
  contexts.forEach(context => {
    const size = IMAGE_CONFIG.RESPONSIVE_BREAKPOINTS[context];
    if (size) {
      imageSet[context] = {
        url: getImageUrl(imagePath, { ...options, context, responsive: true }),
        width: size.width,
        height: size.height,
        descriptor: `${size.width}w`
      };
    }
  });
  
  return imageSet;
}

/**
 * Network URL tester
 */
export function testImageUrl(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      logImageDebug('info', `✅ Image loads: ${url}`);
      resolve({ success: true, url });
    };
    img.onerror = () => {
      logImageDebug('error', `❌ Image failed: ${url}`);
      FAILED_URLS.add(url);
      resolve({ success: false, url, error: 'Failed to load' });
    };
    img.src = url;
  });
}

/**
 * 🔥 NEW: Test responsive image generation
 */
export async function testResponsiveGeneration() {
  console.group('🧪 TESTING RESPONSIVE IMAGE GENERATION');
  
  const testImages = [
    'mark-francisco.jpg',
    'american-cultural-faux-pas.jpg',
    'fika-coffee-meeting-story.jpg'
  ];
  
  for (const image of testImages) {
    console.group(`Testing: ${image}`);
    
    // Test different contexts
    const contexts = ['thumbnail', 'small', 'medium', 'large'];
    
    for (const context of contexts) {
      const url = getBlogImageUrl(image, { context });
      console.log(`${context}: ${url}`);
      
      // Test if it loads
      const result = await testImageUrl(url);
      console.log(`  ${result.success ? '✅' : '❌'} ${context}`);
    }
    
    console.groupEnd();
  }
  
  console.groupEnd();
}

/**
 * Debug functions
 */
export function setDebugMode(enabled) {
  IMAGE_CONFIG.DEBUG_MODE = enabled;
  console.log(`🔧 Debug mode ${enabled ? 'ENABLED' : 'DISABLED'}`);
}

export function clearCache() {
  URL_CACHE.clear();
  LOGGED_URLS.clear();
  FAILED_URLS.clear();
  cachedBaseUrl = null;
  webpSupported = null;
  console.log('🧹 All caches cleared');
}

export function getImageConfigStatus() {
  return {
    isDevelopment,
    isProduction,
    usingGoogleCloudStorage: IMAGE_CONFIG.USE_GOOGLE_CLOUD_STORAGE,
    baseUrl: getImageBaseUrl(),
    webpSupported: supportsWebP(),
    debugMode: IMAGE_CONFIG.DEBUG_MODE,
    cacheSize: URL_CACHE.size,
    loggedUrls: LOGGED_URLS.size,
    failedUrls: FAILED_URLS.size,
    responsiveBreakpoints: IMAGE_CONFIG.RESPONSIVE_BREAKPOINTS,
    contextSizes: IMAGE_CONFIG.CONTEXT_SIZES,
    config: IMAGE_CONFIG
  };
}

// Keep all existing utility functions
export function getPlaceholderImageUrl(type = 'blog') {
  return IMAGE_CONFIG.FALLBACK_IMAGES[type] || IMAGE_CONFIG.FALLBACK_IMAGES.blog;
}

export function getImageSizes(imageType = 'card') {
  const sizeMap = {
    hero: '(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 1200px',
    card: '(max-width: 640px) 100vw, (max-width: 768px) 50vw, 400px',
    thumbnail: '(max-width: 640px) 80vw, 200px',
    full: '100vw'
  };
  
  return sizeMap[imageType] || sizeMap.card;
}

export function getOptimizedDimensions(imageType = 'card', isMobile = false) {
  const dimensionMap = {
    hero: { 
      width: isMobile ? 800 : 1200, 
      height: isMobile ? 600 : 800, 
      quality: 85 
    },
    card: { 
      width: isMobile ? 400 : 600, 
      height: isMobile ? 300 : 400, 
      quality: 80 
    },
    thumbnail: { 
      width: isMobile ? 150 : 200, 
      height: isMobile ? 150 : 200, 
      quality: 75 
    }
  };
  
  return dimensionMap[imageType] || dimensionMap.card;
}

export function generateImageAltText(title, category = '') {
  const baseAlt = title || 'Connect & Co image';
  const categoryText = category ? ` - ${category}` : '';
  return `${baseAlt}${categoryText} | Connect & Co community`;
}

export function preloadImages(imageUrls) {
  if (typeof window === 'undefined') return;
  
  imageUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = 'image';
    document.head.appendChild(link);
  });
}

export function useResponsiveImage(imagePath, imageType = 'card') {
  const dimensions = getOptimizedDimensions(imageType);
  const mobileDimensions = getOptimizedDimensions(imageType, true);
  
  const src = getImageUrl(imagePath, dimensions);
  const mobileSrc = getImageUrl(imagePath, mobileDimensions);
  
  const srcSet = `${mobileSrc} ${mobileDimensions.width}w, ${src} ${dimensions.width}w`;
  const sizes = getImageSizes(imageType);
  
  return {
    src,
    srcSet,
    sizes,
    alt: generateImageAltText(imagePath.split('/').pop()?.split('.')[0] || '')
  };
}

// Enhanced debug panel
if (isDevelopment && typeof window !== 'undefined') {
  window.connectcoImageDebug = {
    config: getImageConfigStatus,
    testUrl: testImageUrl,
    testResponsive: testResponsiveGeneration, // 🔥 NEW
    generate: getImageUrl,
    blog: getBlogImageUrl,
    author: getAuthorImageUrl,
    event: getEventImageUrl,
    venue: getVenueImageUrl,
    responsive: getResponsiveImageSet, // 🔥 NEW
    setDebug: setDebugMode,
    clearCache: clearCache,
    analyze: analyzeImageInput,
    webpSupported: supportsWebP
  };
  
  console.log('🛠️ Enhanced debug panel: window.connectcoImageDebug');
  console.log('🧪 Test responsive: window.connectcoImageDebug.testResponsive()');
}

// Export configuration for debugging
export { IMAGE_CONFIG };

// Default export with all utilities
export default {
  getImageUrl,
  getBlogImageUrl,
  getAuthorImageUrl,
  getEventImageUrl,
  getVenueImageUrl,
  getBrandingImageUrl,
  getResponsiveImageSet, // 🔥 NEW
  getPlaceholderImageUrl,
  getImageSizes,
  getOptimizedDimensions,
  generateImageAltText,
  preloadImages,
  useResponsiveImage,
  supportsWebP,
  testImageUrl,
  testResponsiveGeneration, // 🔥 NEW
  getImageConfigStatus,
  setDebugMode,
  clearCache,
  analyzeImageInput
};